<template>
  <ElForm
    :model="event"
    :rules="detailsFormRules"
    ref="event-edit-form"
    label-position="top"
    status-icon
    class="w-full"
    @submit.native.prevent
  >
    <ElFormItem
      label="Name"
      prop="title"
    >
      <ElInput
        ref="input-event-title"
        v-model="event.title"
        :placeholder="titlePlaceholder"
      />
    </ElFormItem>
    <ElFormItem
      label="Venue"
      prop="venue"
    >
      <UIInputGoogleAutocomplete
        v-model="event.venue"
        :placeholder="venuePlaceholder"
        @input="event.googlePlaceId = ''"
        @google-place-id-update="event.googlePlaceId = $event"
      />
    </ElFormItem>

    <ElFormItem
      label="Start date"
      prop="datePeriod.start.date"
      class="form-item-date-time"
    >
      <div class="flex flex-col items-center mobile-m:flex-row">
        <ElDatePicker
          v-model="event.datePeriod.start.date"
          :format="dateTimeFormatting.date"
          :disabled="existingEventData && existingEventData.status === 1"
          :clearable="false"
          :editable="false"
          :picker-options="startingDatePickerOptions"
          type="date"
          class="input-date-time"
          @change="onStartingDateChange"
        />
        <p class="hidden text-xl px-10 mobile-m:block">-</p>
        <ElTimePicker
          v-model="event.datePeriod.start.time"
          :format="dateTimeFormatting.time"
          :disabled="existingEventData && existingEventData.status === 1"
          :clearable="false"
          :editable="false"
          class="input-date-time mt-5 mobile-m:mt-0"
        />
      </div>
      <p class="text-primary leading-normal text-right italic mt-5 mobile-l:text-left">
        Time zone: {{ event.datePeriod.start.offset }}
      </p>
    </ElFormItem>

    <ElFormItem
      label="End date"
      prop="datePeriod.end.date"
    >
      <div class="flex flex-col items-center mobile-m:flex-row">
        <ElDatePicker
          v-model="event.datePeriod.end.date"
          :format="dateTimeFormatting.date"
          :disabled="existingEventData && existingEventData.status === 1"
          :clearable="false"
          :editable="false"
          :picker-options="endingDatePickerOptions"
          type="date"
          class="input-date-time"
        />
        <p class="hidden text-xl px-10 mobile-m:block">-</p>
        <ElTimePicker
          v-model="event.datePeriod.end.time"
          :format="dateTimeFormatting.time"
          :disabled="existingEventData && existingEventData.status === 1"
          :clearable="false"
          :editable="false"
          class="input-date-time mt-5 mobile-m:mt-0"
        />
      </div>
      <p class="text-primary leading-normal text-right italic mt-5 mobile-l:text-left">
        Time zone: {{ event.datePeriod.end.offset }}
      </p>
    </ElFormItem>

    <ElFormItem prop="firstNameFirstOrder">
      <template slot="label">
        Alphabetic sorting
        <ElPopover
          placement="top"
          title="Alphabetic sorting"

          trigger="hover"
          content="By choosing the sorting preference you define the way your guest names
            will be listed on the guest list."
          popper-class="w-400 until-mobile-m:w-250 left-12"
        >
          <FontAwesomeIcon
            slot="reference"
            :icon="['fas', 'info-circle']"
            class="text-xl text-primary ml-5"
          />
        </ElPopover>
      </template>
      <ElRadioGroup v-model="event.firstNameFirstOrder">
        <ElRadioButton :label="true">By first name</ElRadioButton>
        <ElRadioButton :label="false">By last name</ElRadioButton>
      </ElRadioGroup>
    </ElFormItem>

    <ElFormItem prop="description">
      <template slot="label">
        More info <span class="font-normal text-sm italic">- Optional</span>
      </template>
      <ElInput
        v-model="event.description"
        :rows="2"
        :autosize="{ minRows: 2, maxRows: 6 }"
        type="textarea"
        :placeholder="descriptionPlaceholder"
        class="input-description"
      />
    </ElFormItem>

    <UIToggle
      v-model="advancedOptionsIsOpen"
      ref="additional-infos"
      label="Additional guest infos"
      label-classes="w-full flex items-center py-8 rounded-t-5 border-b border-gray-200 transition-colors
      duration-200 ease-in-out-quart focus:outline-none focus:border-gray-400 hover:border-gray-400 mobile-m:px-0"
      class="mt-25"
    >
      <template #label>
        <h3 class="text-lg">
          Advanced options <span class="font-normal text-sm italic">- Optional</span>
        </h3>
        <FontAwesomeIcon
          :icon="['far', 'chevron-right']"
          class="text-base text-primary-light ml-20 transform rotate-0 duration-300 ease-in-out-quart"
          :class="{ 'rotate-90': advancedOptionsIsOpen }"
        />
      </template>
      <ElFormItem
        prop="customBarcodeValue"
        class="mt-10"
      >
        <template slot="label">
          <span class="flex flex-col items-start mobile-l:flex-row mobile-l:items-end">
            Custom barcode
            <span>
              <span class="text-gray-500 my-5 mobile-l:my-0 mobile-l:ml-5">(Ex. Ticket ID, Member ID)</span>
              <ElPopover
                placement="top"
                title="Custom barcode"

                trigger="hover"
                content="By selecting this option you can set a custom barcode value when adding/importing your
                guests (Ticket ID, Member ID, etc...)"
                popper-class="w-400 until-mobile-m:w-250 left-12"
              >
                <FontAwesomeIcon
                  slot="reference"
                  :icon="['fas', 'info-circle']"
                  class="text-xl text-primary ml-5"
                />
              </ElPopover>
            </span>
          </span>
        </template>
        <ElSwitch v-model="event.customBarcodeValue" />
      </ElFormItem>

      <TransitionCollapse>
        <ElFormItem
          v-if="event.customBarcodeValue"
          prop="barcodeType"
          class="mt-10"
        >
          <template slot="label">
            Barcode type
            <span class="text-gray-500 tracking-wide ml-10">BETA</span>
            <ElPopover
              placement="top"
              title="Barcode type"

              trigger="hover"
              content="Select the barcode type, QR-Code (default) or Code39. Can be scanned by the
              Eventor iOS App only."
              popper-class="w-400 until-mobile-m:w-250 left-12"
            >
              <FontAwesomeIcon
                slot="reference"
                :icon="['fas', 'info-circle']"
                class="text-xl text-primary ml-5"
              />
            </ElPopover>
          </template>
          <ElRadioGroup v-model="event.barcodeType">
            <ElRadioButton label="QRCode">
            <span class="inline-flex flex-col items-center">
              QR-Code
              <FontAwesomeIcon
                :icon="['fal', 'qrcode']"
                class="text-3xl mt-5"
              />
            </span>
            </ElRadioButton>
            <ElRadioButton label="Code39">
            <span class="inline-flex flex-col items-center">
              Code39
              <FontAwesomeIcon
                :icon="['fal', 'barcode']"
                class="text-3xl mt-5"
              />
            </span>
            </ElRadioButton>
          </ElRadioGroup>
        </ElFormItem>
      </TransitionCollapse>
    </UIToggle>

    <div class="flex justify-end mt-30 laptop:flex-row-reverse">
      <ElButton
        type="text"
        class="mr-20 laptop:mr-0 laptop:ml-20"
        @click="$router.back()"
      >
        Cancel
      </ElButton>
      <ElButton
        class="text-white bg-secondary py-12 px-35 border-2 border-secondary
        hover:text-white hover:bg-secondary hover:border-secondary hover:opacity-75
        focus:text-white focus:bg-secondary focus:border-secondary focus:opacity-75"
        @click="onSubmitPress"
      >
        {{ submitButtonLabel }}
      </ElButton>
    </div>
  </ElForm>
</template>

<script>
import { cloneDeep, debounce } from 'lodash-es';
import {
  eventTitlePlaceholder,
  eventDescriptionPlaceholder,
  venuePlaceholder,
} from '@/utils/placeholders';
import { FBModule, FBFirestoreRef } from '@/helpers/firebaseServices';
import { APICreateEvent } from '@/api';
import {
  systemIsSupportingIntl, systemIsHour12, getSystemLocale, getSystemTimeZone,
} from '@/utils/intl';
import UIToggle from '@/components/UI/UIToggle';
import UIInputGoogleAutocomplete from '@/components/UI/UIInputGoogleAutocomplete';
import TransitionCollapse from '@/components/transition/TransitionCollapse';

const maxEventDuration = 4;
const timezoneFormat = { // TODO: Extract
  day: '2-digit',
  timeZoneName: 'short',
};
const firstWeekDayIndex = systemIsHour12 ? 7 : 1;
const defaultEventData = {
  id: '',
  title: '',
  venue: '',
  googlePlaceId: '',
  startingTime: null,
  endingTime: null,
  datePeriod: {
    start: {
      date: null,
      time: null,
      offset: '',
    },
    end: {
      date: null,
      time: null,
      offset: '',
    },
  },
  description: '',
  firstNameFirstOrder: true,
  customBarcodeValue: false,
  barcodeType: 'QRCode',
};

export default {
  props: {
    existingEventData: {
      type: Object,
    },
  },
  components: {
    UIToggle,
    UIInputGoogleAutocomplete,
    TransitionCollapse,
  },
  data() {
    const checkTitle = (rule, value, callback) => {
      if (!value.trim() || value.trim() === '') {
        callback('Enter the Event name');
      } else if (value.trim().length > 128) {
        callback('Your Event name is too long (more than 128 characters)');
      }
      callback();
    };
    const checkVenue = (rule, value, callback) => {
      if (!value.trim() || value.trim() === '') {
        callback('Enter the Event venue');
      } else if (value.trim().length > 256) {
        callback('Your Event venue is too long (more than 256 characters)');
      }
      callback();
    };
    const checkDescription = (rule, value, callback) => {
      if (value.trim().length > 256) {
        callback('Your Event description is too long (more than 256 characters)');
      }
      callback();
    };
    const checkDateTime = (rule, value, callback) => {
      callback();
    };
    const eventDetails = cloneDeep(defaultEventData);

    let eventStartingTime = null;
    let eventEndingTime = null;
    let eventStartingTimeTimeZone = '';
    let eventEndingTimeTimeZone = '';

    if (!this.existingEventData) {
      const tomorrowStart = new Date();

      tomorrowStart.setDate(tomorrowStart.getDate() + 1);

      // A new event runs until the same time the next day. Ending it a few hours after it starts
      // archived events the same evening, which left people wondering where their event went.
      // Anyone who knows their real end time can still shorten it.
      const tomorrowEnd = new Date(tomorrowStart);

      tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);

      eventStartingTime = tomorrowStart;
      eventEndingTime = tomorrowEnd;
    } else {
      eventStartingTime = this.existingEventData.startingTime.toDate();
      eventEndingTime = this.existingEventData.endingTime.toDate();
    }

    // Setting start and ending timezones
    if (!systemIsSupportingIntl) {
      eventStartingTimeTimeZone = eventStartingTime.toLocaleString(getSystemLocale(), timezoneFormat).substring(4);
      eventEndingTimeTimeZone = eventEndingTime.toLocaleString(getSystemLocale(), timezoneFormat).substring(4);
    } else {
      const intlTimezoneFormatter = new Intl.DateTimeFormat(getSystemLocale(), timezoneFormat);

      eventStartingTimeTimeZone = intlTimezoneFormatter.format(eventStartingTime).substring(4);
      eventEndingTimeTimeZone = intlTimezoneFormatter.format(eventEndingTime).substring(4);
    }

    eventDetails.datePeriod = {
      start: {
        date: eventStartingTime,
        time: eventStartingTime,
        offset: `${getSystemTimeZone()} (${eventStartingTimeTimeZone})`,
      },
      end: {
        date: eventEndingTime,
        time: eventEndingTime,
        offset: `${getSystemTimeZone()} (${eventEndingTimeTimeZone})`,
      },
    };

    if (this.existingEventData) {
      eventDetails.id = this.existingEventData.id;
      eventDetails.title = this.existingEventData.title;
      eventDetails.venue = this.existingEventData.venue;
      eventDetails.firstNameFirstOrder = this.existingEventData.firstNameFirstOrder;
      eventDetails.googlePlaceId = this.existingEventData.googlePlaceId || '';
      eventDetails.description = this.existingEventData.description || '';
      eventDetails.customBarcodeValue = !!this.existingEventData?.guestIdFieldForCheckIn;
      eventDetails.barcodeType = this.existingEventData?.barcodeType || 'QRCode';
    }

    return {
      event: eventDetails,
      advancedOptionsIsOpen: (!!this.existingEventData?.guestIdFieldForCheckIn),
      dateTimeFormatting: {
        time: systemIsHour12 ? 'h:mm A' : 'HH:mm',
        date: systemIsHour12 ? 'MM/dd/yyyy' : 'dd/MM/yyyy',
      },
      startingDatePickerOptions: {
        firstDayOfWeek: firstWeekDayIndex,
        disabledDate: (date) => this.disabledDateCheck(date, true),
      },
      endingDatePickerOptions: {
        firstDayOfWeek: firstWeekDayIndex,
        disabledDate: (date) => this.disabledDateCheck(date),
      },
      detailsFormRules: {
        title: [{
          validator: checkTitle,
          trigger: 'blur',
        }],
        venue: [{
          validator: checkVenue,
          trigger: 'blur',
        }],
        description: [{
          validator: checkDescription,
          trigger: 'blur',
        }],
        'datePeriod.start.date': [{
          validator: checkDateTime,
          trigger: 'blur',
        }],
        'datePeriod.end.date': [{
          validator: checkDateTime,
          trigger: 'blur',
        }],
      },
    };
  },
  mounted() {
    // Auto-focus first input of the form
    if (!this.isCreateEvent) return;
    this.$refs['input-event-title'].focus();
  },
  watch: {
    'event.title': {
      immediate: true,
      // Every keystroke used to commit two mutations, which flooded the LogRocket
      // session log until it suppressed further events
      handler(newTitle) { // TODO: Handle through VueX actions
        // An immediate watcher runs before created(), so build the debounced call on first use
        if (!this.debouncedNavigationTitleUpdate) {
          this.debouncedNavigationTitleUpdate = debounce(this.updateNavigationTitle, 400);
        }

        this.debouncedNavigationTitleUpdate(newTitle);
      },
    },
  },
  computed: {
    // Empty until the edge answers, then filled in: a placeholder never changes from one
    // city to another in front of the user
    place() {
      return this.$store.getters['currency/place'];
    },
    titlePlaceholder() {
      return this.$store.state.currency.isResolved ? eventTitlePlaceholder(this.place) : '';
    },
    venuePlaceholder() {
      return this.$store.state.currency.isResolved ? venuePlaceholder(this.place) : '';
    },
    descriptionPlaceholder() {
      return this.$store.state.currency.isResolved ? eventDescriptionPlaceholder(this.place) : '';
    },
    navigationTitleBase() {
      return this.$store.state.navigation.title.currentEvent.base;
    },
    submitButtonLabel() {
      return this.isCreateEvent ? 'Create event' : 'Update event';
    },
    isCreateEvent() {
      return !this.existingEventData;
    },
  },
  beforeDestroy() {
    if (this.debouncedNavigationTitleUpdate) this.debouncedNavigationTitleUpdate.cancel();
  },
  methods: {
    updateNavigationTitle(newTitle) {
      if (newTitle) {
        this.$store.commit('navigation/SET_CURRENT_NAVIGATION_TITLE_SUFFIX', newTitle);

        const titleBase = this.isCreateEvent ? 'Creating: ' : 'Editing: ';
        if (this.navigationTitleBase !== titleBase) {
          this.$store.commit('navigation/SET_CURRENT_NAVIGATION_TITLE_BASE', titleBase);
        }
      } else {
        this.$store.commit('navigation/RESET_NAVIGATION_TITLE_BASE');
        this.$store.commit('navigation/SET_CURRENT_NAVIGATION_TITLE_SUFFIX', '');
      }
    },
    disabledDateCheck(date, startPicker) {
      const startDateAtMidnight = new Date(this.event.datePeriod.start.date);
      const yesterday = new Date();

      startDateAtMidnight.setHours(0, 0, 0, 0);
      yesterday.setDate(yesterday.getDate() - 1);

      return startPicker ? date < yesterday : date < startDateAtMidnight
        || date > new Date(this.event.datePeriod.start.date.getTime() + (maxEventDuration * 24 * 60 * 60 * 1000));
    },
    onStartingDateChange() {
      const newEndDate = new Date(this.event.datePeriod.start.date);

      // Add a day to the end date (Minimum offset)
      this.event.datePeriod.end.date = newEndDate.setDate(newEndDate.getDate() + 1);
    },
    async onSubmitPress() {
      const formIsValidated = await this.$refs['event-edit-form'].validate().then((valid) => valid).catch((error) => error);

      if (!formIsValidated) return;

      this.$emit('update:is-loading', true);

      const datePeriodStartDate = new Date(this.event.datePeriod.start.date);
      const datePeriodStartTime = new Date(this.event.datePeriod.start.time);

      const datePeriodEndDate = new Date(this.event.datePeriod.end.date);
      const datePeriodEndTime = new Date(this.event.datePeriod.end.time);

      const startingTime = new Date(
        datePeriodStartDate.getFullYear(),
        datePeriodStartDate.getMonth(),
        datePeriodStartDate.getDate(),
        datePeriodStartTime.getHours(),
        datePeriodStartTime.getMinutes(),
        datePeriodStartTime.getSeconds(),
        datePeriodStartTime.getMilliseconds(),
      );
      const endingTime = new Date(
        datePeriodEndDate.getFullYear(),
        datePeriodEndDate.getMonth(),
        datePeriodEndDate.getDate(),
        datePeriodEndTime.getHours(),
        datePeriodEndTime.getMinutes(),
        datePeriodEndTime.getSeconds(),
        datePeriodEndTime.getMilliseconds(),
      );
      const eventData = {
        title: this.event.title.trim(),
        venue: this.event.venue.trim(),
        startingTime,
        endingTime,
        startingTimezone: getSystemTimeZone(),
        endingTimezone: getSystemTimeZone(),
        firstNameFirstOrder: this.event.firstNameFirstOrder,
      };
      if (this.event.googlePlaceId) eventData.googlePlaceId = this.event.googlePlaceId;
      if (this.event.description) eventData.description = this.event.description.trim();

      if (this.isCreateEvent) {
        if (this.event.customBarcodeValue) {
          eventData.guestIdFieldForCheckIn = 'barcodeValue';
          if (this.event.barcodeType !== 'QRCode') eventData.barcodeType = this.event.barcodeType;
        }

        APICreateEvent(eventData)
          .then((response) => {
            const { dataLayer } = window;
            dataLayer.push({ event: 'event-is-created' });

            this.$emit('submit-successful', response.data.eventId);
          })
          .catch((error) => {
            this.$emit('update:is-loading', false);
            this.$emit('submit-failed', error);
          });

        return;
      }

      // Only update Firestore doc here
      if (!eventData.googlePlaceId) eventData.googlePlaceId = FBModule.firestore.FieldValue.delete();
      if (this.event.customBarcodeValue) {
        eventData.guestIdFieldForCheckIn = 'barcodeValue';
        if (this.event.barcodeType === 'QRCode') {
          eventData.barcodeType = FBModule.firestore.FieldValue.delete();
        } else {
          eventData.barcodeType = this.event.barcodeType;
        }
      } else {
        eventData.guestIdFieldForCheckIn = FBModule.firestore.FieldValue.delete();
        eventData.barcodeType = FBModule.firestore.FieldValue.delete();
      }

      FBFirestoreRef.collection('events').doc(this.existingEventData.id)
        .update(eventData)
        .then(() => {
          this.$emit('submit-successful', this.existingEventData.id);
        })
        .catch((error) => {
          this.$emit('update:is-loading', false);
          this.$emit('submit-failed', error);
        });
    },
  },
};
</script>

<style scoped>
.form-item-date-time {
  @apply mb-10;
}

.input-date-time {
  @apply w-full;

  @screen mobile-m {
    @apply w-1/2;
  }

  @screen mobile-l {
    @apply w-auto;
  }
}

.input-description {
  @apply h-auto;
}
</style>
